import { useAction } from "@gadgetinc/react";
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import ButtonToggleGroup from "../../externalComponents/flow_editor/shared/panel/components/ButtonToggleGroup";
import { Field } from "../../externalComponents/tailwind/fieldset";
import { Input } from "../../externalComponents/tailwind/input";
import { Link } from "../../externalComponents/tailwind/link";
import { Text } from "../../externalComponents/tailwind/text";
import { Textarea } from "../../externalComponents/tailwind/textarea";
import { CustomerContext } from "../../providers";
import posthog from "posthog-js";

export default function CreateOptInPage() {
  const navigate = useNavigate();
  const { user, integrations } = useContext(CustomerContext);

  const [, createScenario] = useAction(api.scenario.create);
  const [, createFlowNode] = useAction(api.flowNode.create);

  const [internalName, setInternalName] = useState("");
  const handleChangeInternalName = useCallback((newValue) => {
    setInternalName(newValue);
  }, []);
  const [message, setMessage] = useState("");
  const handleChangeMessage = useCallback((newValue) => {
    setMessage(newValue);
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  // New state for trigger type selection
  const [triggerType, setTriggerType] = useState("");
  const [isAutomaticReply, setIsAutomaticReply] = useState(null);

  const createChatbot = async (config) => {
    if (internalName) {
      setIsLoading(true);

      const scenarioResponse = await createScenario({
        scenario: {
          type: "chatbot",
          customer: { _link: user.customerId },
          name: config.name,
          ...(config.chatbotType && { chatbotType: config.chatbotType }),
          ...(config.createCampaign && {
            campaign: {
              create: {
                name: config.name,
                customer: { _link: user.customerId },
                trigger: config.campaignTrigger,
              },
            },
          }),
        },
      });

      const flowNodeResponse = await createFlowNode({
        flowNode: {
          customer: { _link: user.customerId },
          positionX: 0,
          positionY: 0,
          type: "whatsapp_trigger",
          scenario: { _link: scenarioResponse.data.id },
          whatsappTrigger: {
            create: {
              customer: { _link: user.customerId },
              type: config.triggerType,
              text: config.triggerText,
              status: config.setAutomaticReply ? "inactive" : "active",
            },
          },
        },
      });

      await api.scenario.update(scenarioResponse.data?.id, {
        rootNode: { _link: flowNodeResponse.data?.id },
        status: config.setAutomaticReply ? "inactive" : "active",
      });

      if (config.setWhatsAppOrderNode) {
        await api.customer.update(user.customerId, {
          whatsappOrderNode: { _link: flowNodeResponse.data?.id },
        });
      }

      if (config.createBsuidMessage) {
        const messageNodeResponse = await createFlowNode({
          flowNode: {
            customer: { _link: user.customerId },
            positionX: 500,
            positionY: 0,
            type: "message_skeleton",
            scenario: { _link: scenarioResponse.data.id },
            messageSkeleton: {
              create: {
                customer: { _link: user.customerId },
                body: "Please accept sharing your phoneNumber to keep talking with us",
              },
            },
          },
        });

        await api.flowEdge.create({
          flowEdge: {
            scenario: { _link: scenarioResponse.data.id },
            sourceNode: { _link: flowNodeResponse.data?.id },
            targetNode: { _link: messageNodeResponse.data?.id },
          },
        });
      }

      posthog.capture("create_flow", {
        source: "from-scratch",
        type: "chatbot",
        triggerType: config.posthogTriggerType,
      });

      setIsLoading(false);
      navigate("/chatbots/edit/" + scenarioResponse.data?.id);

      return scenarioResponse;
    }
  };

  const handleCreateIncomingMessage = async () => {
    await createChatbot({
      name: (isAutomaticReply ? "[AutoReply] " : "") + internalName,
      chatbotType: isAutomaticReply ? "autoreply" : undefined,
      triggerType: "exact_match",
      triggerText: isAutomaticReply ? "" : message,
      setAutomaticReply: isAutomaticReply,
      posthogTriggerType: "incoming_message",
    });
  };

  const handleCreateBsuidChatbot = async () => {
    await createChatbot({
      name: "[Missing phone number] " + internalName,
      chatbotType: "bsuid",
      triggerType: "exact_match",
      triggerText: "",
      setAutomaticReply: true,
      createBsuidMessage: true,
      posthogTriggerType: "bsuid",
    });
  };

  const handleCreateWhatsAppOrder = async () => {
    const chatbotName = "[WhatsApp Order] " + internalName;

    // Create the chatbot with campaign
    await createChatbot({
      name: chatbotName,
      chatbotType: "whatsapp_order",
      triggerType: "order",
      triggerText: "",
      setAutomaticReply: false,
      setWhatsAppOrderNode: true,
      posthogTriggerType: "whatsapp_order",
      createCampaign: true,
      campaignTrigger: "whatsapp_order",
    });
  };

  const isCreateDisabled =
    isLoading ||
    internalName === "" ||
    triggerType === "" ||
    (triggerType === "incoming_message" && isAutomaticReply === null) ||
    (triggerType === "incoming_message" &&
      isAutomaticReply === false &&
      message === "");

  const handlePrimaryAction = async () => {
    if (triggerType === "incoming_message") {
      await handleCreateIncomingMessage();
    } else if (triggerType === "whatsapp_order") {
      await handleCreateWhatsAppOrder();
    } else if (triggerType === "bsuid") {
      await handleCreateBsuidChatbot();
    }
  };

  const triggerOptions = [
    {
      id: "incoming_message",
      title: "Incoming message",
      description:
        "Triggered when a contact sends a message (unexpected message, click-to-WhatsApp...)",
    },
    {
      id: "whatsapp_order",
      title: "WhatsApp order",
      description:
        "Triggered when a contact places an order through WhatsApp catalog",
    },
    {
      id: "bsuid",
      title: "Missing phone number",
      description:
        "Triggered when a contact only shared their WhatsApp ID, not a phone number (e.g. click-to-WhatsApp ads).",
    },
  ];

  return (
    <>
      <PageHeading
        title="New chatbot"
        primaryAction="Create"
        isPrimaryDisabled={isCreateDisabled}
        isPrimaryLoading={isLoading}
        handlePrimaryAction={handlePrimaryAction}
      />
      <OverflowFrame>
        <div className="max-w-3xl mx-auto">
          {/* Trigger Type Selection with Cards */}
          <Field>
            {/* Chatbot Internal Name - Always shown */}
            <Field>
              <Text className="text-xl font-semibold my-3">
                Chatbot internal name
              </Text>
              <Input
                name="Document name"
                value={internalName}
                onChange={(e) => handleChangeInternalName(e.target.value)}
                placeholder="Email Newsletter Optin Campaign"
                autoComplete="off"
                maxLength={64}
              />
            </Field>

            <Text className="text-xl font-semibold my-3">Trigger type</Text>
            <div className="grid grid-cols-1 gap-4 w-full justify-items-center sm:grid-cols-2 my-6">
              {triggerOptions.map((option) => {
                const isSelected = triggerType === option.id;
                return (
                  <div
                    key={option.id}
                    onClick={() => setTriggerType(option.id)}
                    className={`hover:cursor-pointer border rounded-customBorderRadius shadow-shadowbuttonmin hover:shadow-shadowbuttonpink overflow-hidden w-full transition-all duration-200 ${
                      isSelected
                        ? "border-pink shadow-shadowbuttonpink"
                        : "border-gray-200"
                    }`}
                  >
                    <div className="flex flex-col justify-between items-center h-40 w-full p-5">
                      <div className="text-center">
                        <Text
                          className={`text-2xl font-bold ${isSelected ? "text-pink-700" : "text-gray-900"}`}
                        >
                          {option.title}
                        </Text>
                      </div>
                      <div className="mt-3">
                        <Text
                          className={`text-sm text-center ${isSelected ? "text-pink-600" : "text-gray-600"}`}
                        >
                          {option.description}
                        </Text>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Field>

          {/* Conditional form based on trigger type */}
          {triggerType === "incoming_message" && (
            <>
              <Text className="text-lg font-semibold mt-2 -mb-5">
                Message trigger
              </Text>
              <ButtonToggleGroup
                options={[
                  { value: false, label: "A specific message" },
                  { value: true, label: "Any message" },
                ]}
                selectedValue={isAutomaticReply}
                onSelect={(value) => setIsAutomaticReply(value)}
                className="mb-3"
              />

              <br />
              {isAutomaticReply === false && (
                <Field>
                  <Text className="text font-semibold my-2">
                    Pre-written message when contact clicks on link
                  </Text>
                  <Textarea
                    name="Message body"
                    placeholder="Message body"
                    value={message}
                    onChange={(e) => handleChangeMessage(e.target.value)}
                    maxLength={256}
                    autoComplete="off"
                  />
                </Field>
              )}
            </>
          )}

          {triggerType === "whatsapp_order" && (
            <Field>
              <Text className="text-lg font-semibold mb-3">Configuration</Text>
              <Text className="text-sm text-gray-600">
                WhatsApp order chatbots are automatically triggered when
                customers place orders through your WhatsApp catalog. You should
                have enabled WhatsApp catalog in your WhatsApp Business account.
              </Text>
              {integrations?.shopify && (
                <Text className="text-sm text-gray-600 mt-2">
                  You can sync your Shopify catalog with WhatsApp easily from{" "}
                  <Link
                    href="https://business.facebook.com/latest/whatsapp_manager/catalog"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    here
                  </Link>
                </Text>
              )}
            </Field>
          )}

          {triggerType === "bsuid" && (
            <Field>
              <Text className="text-lg font-semibold mb-3">Configuration</Text>
              <Text className="text-sm text-gray-600">
                This chatbot fires for any message from a contact who has only
                shared their WhatsApp ID (bsuid), not a phone number. We&apos;ve
                pre-built a message asking them to share their phone number,
                with a non-removable button for it - only one of these chatbots
                can be active at a time. The chatbot, configured to launch on a
                contact's first message, triggers when the user provides their
                phone number.
              </Text>
            </Field>
          )}
        </div>
      </OverflowFrame>
    </>
  );
}
